import { Skeleton } from "@/components/ui/skeleton";

type Variant = "auth" | "billing" | "pay" | "dashboard" | "default";

interface RouteFallbackProps {
  variant?: Variant;
}

/**
 * Layout-aware Suspense fallback so users see a skeleton matching the
 * destination route instead of a blank screen during chunk loads.
 */
export default function RouteFallback({ variant = "default" }: RouteFallbackProps) {
  if (variant === "auth") {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row bg-surface-muted">
        <div className="lg:w-[60%] w-full bg-gradient-to-br from-hibane-navy to-hibane-dark-blue min-h-[200px] lg:min-h-screen" />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-4">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-10 w-full mt-6" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "billing") {
    return (
      <div className="min-h-screen bg-surface-muted p-6 space-y-6">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-5 w-80" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-card" />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-card" />
      </div>
    );
  }

  if (variant === "pay") {
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-4 bg-surface rounded-card p-8 shadow-card">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-64" />
          <div className="space-y-2 mt-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
          <Skeleton className="h-12 w-full mt-6" />
        </div>
      </div>
    );
  }

  if (variant === "dashboard") {
    return (
      <div className="min-h-screen flex bg-surface-muted">
        <div className="hidden md:flex w-60 flex-col bg-gradient-to-b from-desk-navy to-desk-navy-end p-3 gap-2">
          <Skeleton className="h-10 w-full bg-white/10" />
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full bg-white/10" />
          ))}
        </div>
        <div className="flex-1 p-6 space-y-6">
          <Skeleton className="h-24 w-full rounded-card" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-card" />
            ))}
          </div>
          <Skeleton className="h-80 w-full rounded-card" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-muted p-6 space-y-6">
      <Skeleton className="h-9 w-56" />
      <Skeleton className="h-72 w-full rounded-card" />
    </div>
  );
}